import { LoaderCircle, ShieldAlert } from "lucide-react"
import { useState } from "react"
import type { EmbeddedBrowserCertificateError } from "../../electronAPIs/browser"

type BrowserCertificateWarningProps = {
    warning: EmbeddedBrowserCertificateError
    onGoBack: () => Promise<void>
    onProceed: () => Promise<void>
}

function readableCertificateError(error: string): string {
    if (error.includes("AUTHORITY_INVALID")) return "The certificate was issued by an authority this computer does not trust."
    if (error.includes("COMMON_NAME_INVALID")) return "The certificate was issued for a different address."
    if (error.includes("DATE_INVALID")) return "The certificate is expired or not valid yet."
    if (error.includes("REVOKED")) return "The certificate has been revoked."
    return "Chromium could not verify the certificate presented by this site."
}

function certificateDate(value: number | undefined): string | null {
    if (value === undefined) return null
    const date = new Date(value * 1_000)
    return Number.isNaN(date.valueOf()) ? null : date.toLocaleString()
}

export function BrowserCertificateWarning({ warning, onGoBack, onProceed }: BrowserCertificateWarningProps): JSX.Element {
    const [pendingAction, setPendingAction] = useState<"back" | "proceed" | null>(null)
    const [failure, setFailure] = useState<string | null>(null)
    const validStart = certificateDate(warning.validStart)
    const validExpiry = certificateDate(warning.validExpiry)

    const run = async (action: "back" | "proceed") => {
        setFailure(null)
        setPendingAction(action)
        try {
            await (action === "proceed" ? onProceed() : onGoBack())
        } catch {
            setFailure(action === "proceed" ? "That approval expired. Reload the page to request a new one." : "Browser could not leave this page.")
            setPendingAction(null)
        }
    }

    return (
        <div className="absolute inset-0 z-20 overflow-y-auto bg-base-100 text-base-content" role="alert" aria-labelledby="browser-certificate-warning-title">
            <div className="mx-auto flex min-h-full w-full max-w-xl flex-col justify-center px-8 py-10">
                <ShieldAlert size={38} className="mb-5 text-warning" aria-hidden="true" />
                <h2 id="browser-certificate-warning-title" className="text-2xl font-semibold tracking-tight">
                    Your connection isn&apos;t private
                </h2>
                <p className="mt-3 text-sm leading-6 text-muted">
                    {readableCertificateError(warning.error)} Someone could be impersonating{" "}
                    <span className="font-medium text-base-content">{warning.host}</span> or intercepting information you send.
                </p>
                <p className="mt-3 border-l-2 border-warning/60 pl-3 text-xs leading-5 text-muted">
                    Continue only if you trust this device and the network you&apos;re using. This exception stays inside WildLark until the app quits or the
                    Browser profile is replaced, and applies only to this origin and certificate.
                </p>

                <details className="mt-5 border border-border bg-base-200/50 px-3 py-2 text-xs">
                    <summary className="cursor-pointer select-none font-medium text-base-content">Certificate details</summary>
                    <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-muted">
                        <dt>Address</dt>
                        <dd className="min-w-0 break-all font-mono text-base-content">{warning.url}</dd>
                        <dt>Error</dt>
                        <dd className="min-w-0 break-all font-mono text-base-content">{warning.error}</dd>
                        {warning.subjectName && (
                            <>
                                <dt>Subject</dt>
                                <dd className="min-w-0 break-all text-base-content">{warning.subjectName}</dd>
                            </>
                        )}
                        {warning.issuerName && (
                            <>
                                <dt>Issuer</dt>
                                <dd className="min-w-0 break-all text-base-content">{warning.issuerName}</dd>
                            </>
                        )}
                        {validStart && (
                            <>
                                <dt>Valid from</dt>
                                <dd className="text-base-content">{validStart}</dd>
                            </>
                        )}
                        {validExpiry && (
                            <>
                                <dt>Valid until</dt>
                                <dd className="text-base-content">{validExpiry}</dd>
                            </>
                        )}
                    </dl>
                </details>

                {failure && <p className="mt-4 text-xs text-error">{failure}</p>}

                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        className="btn h-9 bg-primary px-4 text-sm font-medium text-primary-content disabled:opacity-50"
                        disabled={pendingAction !== null}
                        onClick={() => void run("back")}
                    >
                        {pendingAction === "back" && <LoaderCircle size={14} className="mr-2 animate-spin" />}
                        Go back
                    </button>
                    <button
                        type="button"
                        className="btn h-9 border border-error/50 px-4 text-sm font-medium text-error hover:bg-error/10 disabled:opacity-50"
                        disabled={pendingAction !== null}
                        onClick={() => void run("proceed")}
                    >
                        {pendingAction === "proceed" && <LoaderCircle size={14} className="mr-2 animate-spin" />}
                        Proceed anyway
                    </button>
                </div>
            </div>
        </div>
    )
}
